import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import { useTheme, Card, CardContent, Chip, Stack, Grid, Typography, Button, Alert } from '@mui/material'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import { formEnsembleBaseUrl } from '../../../../utils/api/servers'

interface EnsembleModalProps {
  open: boolean
  onClose: () => void
  portfolioId: string
  strategyCoin: string
  strategyId: string
  ensembles: Record<string, string>
}

interface StrategyInfo {
  server: string
  code: string
  status: string
  directory: string
  strategy: string
  running: boolean
  pid: string
}

const EnsembleModal: React.FC<EnsembleModalProps> = (
  { open, onClose, portfolioId, strategyCoin, strategyId, ensembles }
) => {
  const theme = useTheme()
  const { isAuthorized, accessToken } = useContext(AuthContext)
  const [baseUrl, setBaseUrl] = useState<string>('-')
  const [infoUrl, setInfoUrl] = useState<string>('-')
  const [strategyInfo, setStrategyInfo] = useState<StrategyInfo | undefined | null>(undefined)

  // Memoize ensembleStrategyKey
  const ensembleStrategyKey = useMemo(() => {
    return ((portfolioId?.trim()) !== '') && ((strategyCoin?.trim()) !== '') && ((strategyId?.trim()) !== '') ? `${portfolioId}.${strategyCoin}.${strategyId}` : '-'
  }, [portfolioId, strategyCoin, strategyId])

  // submit confirm message
  const [showConfirmMsg, setShowConfirmMsg] = useState(false)

  const getStrategyInfo = useCallback(() => {
    // Check for missing or empty required values
    if (![portfolioId, strategyCoin, strategyId].every(value => value !== undefined && value !== null && value.trim() !== '')) {
      setBaseUrl('-')
      setInfoUrl('-')
      setStrategyInfo(null)
      return
    }

    const formedBaseUrl = formEnsembleBaseUrl(ensembles[ensembleStrategyKey])
    setBaseUrl(formedBaseUrl)
    setInfoUrl(formedBaseUrl + '/info?strategy=' + strategyId)
    axios
      .get(formedBaseUrl + '/info?strategy=' + strategyId, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        const data: StrategyInfo = response.data.data
        setStrategyInfo(data)
      })
      .catch(reason => {
        console.error(reason)
        setStrategyInfo(null)
      })
  }, [isAuthorized, accessToken, portfolioId, strategyCoin, strategyId])

  useEffect(() => {
    // get strategy info when modal is opened
    if (open && isAuthorized) {
      getStrategyInfo()
    } else {
      setStrategyInfo(null)
    }

    // hide confirm message when modal is closed
    if (!open) {
      setShowConfirmMsg(false)
    }
  }, [open, isAuthorized, portfolioId, strategyCoin, strategyId])

  const onClickStartButton = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()

      if (!showConfirmMsg) {
        setShowConfirmMsg(true)
        return
      }

      /** Perform start action here **/

      setShowConfirmMsg(false)
    }, [showConfirmMsg])

  const children = useMemo<React.ReactElement>(() => {
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Card>
          <CardContent>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">Strategy:</Typography></Grid>
              <Grid item xs={9}><Typography variant="body2">{ensembleStrategyKey}</Typography></Grid>

              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">Base URL:</Typography></Grid>
              <Grid item xs={9}><Typography variant="body2">{baseUrl}</Typography></Grid>

              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">Info URL:</Typography></Grid>
              <Grid item xs={9}><Typography variant="body2">{infoUrl}</Typography></Grid>

              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">Server:</Typography></Grid>
              <Grid item xs={9}><Typography variant="body2">{strategyInfo?.server ?? '-'}</Typography></Grid>

              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">Directory:</Typography></Grid>
              <Grid item xs={9}><Typography variant="body2">{strategyInfo?.directory ?? '-'}</Typography></Grid>

              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">Status:</Typography></Grid>
              <Grid item xs={9}>
                <Typography variant="body2" color={strategyInfo?.running === true ? 'success.main' : 'error.main'}>
                  {strategyInfo == null ? 'agent not found' : strategyInfo.running ? 'running' : 'stopped'}
                </Typography>
              </Grid>

              <Grid item xs={3}><Typography variant="body2" fontWeight="bold">PID:</Typography></Grid>
              <Grid item xs={9}><Typography variant="body2">{strategyInfo?.pid ?? '-'}</Typography></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          {/* Start Button */}
            <Button
                variant="outlined"
                onClick={onClickStartButton}
                disabled={strategyInfo?.running === true}
            >
                Start
            </Button>

          {/* Confirmation Message Next to Button */}
          {showConfirmMsg && (
            <Stack spacing={2} direction="row">
              <Alert severity="warning">Are you sure you want to start this strategy?</Alert>
              <Button variant="outlined" color="success" onClick={onClickStartButton}>Yes</Button>
              <Button variant="outlined" color="error" onClick={() => { setShowConfirmMsg(false) }}>No</Button>
            </Stack>
          )}
        </Box>
      </Stack>
    )
  }, [strategyInfo, ensembleStrategyKey, baseUrl, infoUrl, showConfirmMsg])

  return (
    <div>
      <Modal
          open={open}
          onClose={onClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '30%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '800px',
            height: '80%',
            maxHeight: '500px',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.primary.main}`,
            boxShadow: 24,
            p: 4,
            color: 'white'
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={portfolioId} variant="outlined" color="primary" />
            <Chip label={strategyCoin} variant="outlined" color="primary" />
            <Chip label={strategyId} variant="outlined" color="primary" />
          </Stack>

          {/* Add space before children */}
          <Box sx={{ mt: 2 }}>{children}</Box>

        </Box>
      </Modal>
    </div>
  )
}

export default EnsembleModal
